import { ConsolidatedLocationView } from '@mark43/cad-api';
import { capitalizeEachWordInString } from '../../common/helpers/typography';
import { LocationStoreView } from '../types/locations';

export function formatFullAddress(
    location?: ConsolidatedLocationView,
    options?: {
        capitalize?: boolean;
        omitPlacename?: boolean;
        omitAddress?: boolean;
    }
): string {
    if (!location) {
        return '';
    }
    const partialAddress = [
        options?.omitPlacename ? undefined : location.placeName,
        options?.omitAddress ? undefined : location.streetAddress?.replace(' & ', '/'),
        location.rangeName ? `[${location.rangeName}]` : undefined,
        location.locality,
    ]
        .filter((s) => !!s)
        .join(', ');

    const addressWithAdminArea = [location.adminArea1, location.postalCode]
        .filter((s) => !!s)
        .join(', ');

    const fullAddress = [
        options?.capitalize
            ? capitalizeEachWordInString(partialAddress, { ignoreWordsOfLength: 2 })
            : partialAddress,
        addressWithAdminArea,
    ]
        .filter((s) => !!s)
        .join(', ');

    if (!location.areCoordinatesVerified) {
        return `${fullAddress} (FORCED)`;
    }

    return fullAddress;
}

export function formatCrossStreets(location?: ConsolidatedLocationView): string {
    if (location == null) {
        return '';
    }
    return [location.crossStreet1, location.crossStreet2].filter((s) => !!s).join('/');
}

export function getCrossStreetsString(
    consolidatedLocationView: ConsolidatedLocationView | null | undefined,
    capitalize?: boolean
): string {
    if (!consolidatedLocationView) return '';

    const fullString = [
        consolidatedLocationView.crossStreet1,
        consolidatedLocationView.crossStreet2,
    ]
        .filter((str) => !!str)
        .join(' / ');

    return capitalize
        ? capitalizeEachWordInString(fullString, { ignoreWordsOfLength: 2 })
        : fullString;
}

export function getLocationSummarySubpremises(
    view?: ConsolidatedLocationView | LocationStoreView | null
): string {
    if (!view) return '';
    const subpremises = [];

    const cap = (s: string) =>
        capitalizeEachWordInString(s, {
            ignoreWordsOfLength: 2,
        });

    for (let i = 1; i < 6; i++) {
        const name = view[`subPremise${i}Name` as keyof ConsolidatedLocationView] as string;
        const value = view[`subPremise${i}Value` as keyof ConsolidatedLocationView] as string;
        if (name && value) {
            subpremises.push(cap(name) + ' ' + cap(value));
        } else {
            if (name) {
                subpremises.push(cap(name));
            }
            if (value) {
                subpremises.push(cap(value));
            }
        }
    }

    return subpremises.join(', ');
}

export function formatLocationSource(view?: ConsolidatedLocationView | LocationStoreView | null) {
    if (!view || !view.source) {
        return '';
    }
    if (view.source === 'FORCED_SUB_PREMISES') {
        return 'Forced Location';
    }
    if (view.source === 'FORCED_LOCATION' && view.resolverSource === 'LAT_LONG') {
        return 'Lat Long';
    }
    return capitalizeEachWordInString(view.source.replace('_', ' '), {
        ignoreWordsOfLength: 2,
    });
}

export const getCrossStreetDisplay = (location: LocationStoreView) => {
    if (location.crossStreet1 && location.crossStreet2) {
        return `${location.crossStreet1}/${location.crossStreet2}`;
    } else if (location.crossStreet1 || location.crossStreet2) {
        return location.crossStreet1 || location.crossStreet2;
    } else {
        return '\u00A0';
    }
};

export function getSubpremisesQueryFromLocation(
    view?: ConsolidatedLocationView | LocationStoreView | null
): string {
    if (!view) {
        return '';
    }

    const subpremises = [];

    for (let i = 1; i < 6; i++) {
        const name = view[`subPremise${i}Name` as keyof ConsolidatedLocationView] as string;
        const value = view[`subPremise${i}Value` as keyof ConsolidatedLocationView] as string;

        if (!name && value) {
            subpremises.push(`#:${value}`);
        } else if (name && value) {
            subpremises.push(`#${name}:${value}`);
        }
    }

    return subpremises.join(' ');
}
